import { useCallback, useEffect, useState, useRef } from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import {
  IntegrationsListResponseSchema,
  WorkspaceIntegration,
} from "../models/integration";

interface UseIntegrationsProps {
  fetchOnLoad?: boolean;
}

export const useIntegrations = ({
  fetchOnLoad = false,
}: UseIntegrationsProps) => {
  const [integrations, setIntegrations] = useState<WorkspaceIntegration[]>([]);
  const [loading, setLoading] = useState(fetchOnLoad);
  const hasFetched = useRef(false);

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: "/workspace/integrations",
      });
      const data = IntegrationsListResponseSchema.parse(response);
      setIntegrations(data.integrations);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteIntegration = useCallback(async (system: string) => {
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/workspace/integrations/${system}`,
      });
      setIntegrations((prev) =>
        prev.filter((i) => i.integrationType !== system),
      );
      toast.success("Integration deleted");
      return { success: true };
    } catch (error) {
      console.error(error);
      toast.error("Error deleting integration");
      return { success: false };
    }
  }, []);

  useEffect(() => {
    if (fetchOnLoad && !hasFetched.current) {
      fetchIntegrations();
      hasFetched.current = true;
    }
  }, [fetchOnLoad, fetchIntegrations]);

  return { integrations, loading, fetchIntegrations, deleteIntegration };
};
